/**
 * Masks credential values in a raw PMS request or response before it is shown.
 *
 * PMS integrations authenticate inside the message body, not in a header. Every
 * Mosafer request carries four credentials in cleartext — SecurityCode and
 * SecurityPass for the property, AgencyCode and AgencyPass for the channel
 * (php/pmsmanager MosaferTrait::buildActionBody) — and other providers send a
 * Password field. Rendering a comlog verbatim therefore puts live passwords on
 * screen for anyone with panel access, and into any ticket the evidence is
 * pasted into.
 *
 * The FIELD NAMES are deliberately kept. "SecurityPass was sent and its value
 * is hidden" is useful to support; "something was sent" is not, and a blank
 * credential — the actual fault on two channels of the property that prompted
 * this screen — is only visible if the field itself still appears.
 */
export const REDACTED = '[redacted]'

/**
 * Matched case-insensitively against the whole field name.
 *
 * A denylist cannot be complete: a provider added later with a differently
 * named credential field will not be covered until its name is added here.
 * That residual is accepted because the alternative — an allowlist over
 * arbitrary third-party payloads — would redact the evidence itself. Add to
 * this list when onboarding a provider that authenticates in the body.
 */
const SECRET_FIELDS = [
  'securitypass',
  'securitycode',
  'agencypass',
  'agencycode',
  'password',
  'passwd',
  'pass',
  'pwd',
  'secret',
  'token',
  'accesstoken',
  'apikey',
  'api_key',
  'authorization',
  'auth'
]

/**
 * Suffix match, not exact match.
 *
 * Exact membership missed every COMPOUND credential name, and the one that
 * mattered most was the commonest field in this estate: Haris sends
 * `"CrsPassword"`, which is not in the list above — only `"password"` is — so
 * it was never redacted. Measured in production: 200 of 200 sampled Haris
 * requests carry that field in cleartext, across 35 hotels, with 365-day
 * retention. Every one of them rendered its live value to anyone with panel
 * access, and into any ticket the evidence was pasted into.
 *
 * A suffix match catches the whole family — CrsPassword, HarisPassword,
 * PmsPass — without an entry per provider, which is what the exact list would
 * have required and would have kept missing.
 *
 * The `passenger` guard still holds, and it is why this is a SUFFIX match and
 * not `includes`: `"passenger".endsWith("pass")` is false (it ends in
 * `"enger"`), while `"passenger".includes("pass")` would be true and would
 * redact a field that is evidence, not a credential. The regression test for
 * that case is the reason to keep the distinction deliberate.
 *
 * Found by security-auditor during /pre-merge and reproduced: the previous
 * exact match left `{"CrsPassword":"..."}` completely unchanged.
 */
const isSecret = (name: string) => {
  const field = name.trim().toLowerCase()

  return SECRET_FIELDS.some(
    (secret) => field === secret || field.endsWith(secret)
  )
}

/**
 * Applied to XML elements, XML attributes and JSON string properties, because
 * the providers between them use all three shapes.
 *
 * Deliberately textual rather than parse-and-reserialise: a payload that fails
 * to parse must still be redacted, and reserialising evidence would change the
 * bytes support is trying to read.
 */
export function redactSecrets(raw: string): string {
  if (raw === '' || raw == null) {
    return raw
  }

  return (
    raw
      // <SecurityPass>value</SecurityPass> and namespaced variants
      .replace(
        /<([A-Za-z_][\w.-]*:)?([\w.-]+)([^>]*)>([^<]*)<\/(?:[A-Za-z_][\w.-]*:)?\2>/g,
        (match, ns, name, attrs, value) =>
          isSecret(name) && value !== ''
            ? `<${ns ?? ''}${name}${attrs}>${REDACTED}</${ns ?? ''}${name}>`
            : match
      )
      // pass="value" / pass='value'
      .replace(
        /([\w.-]+)\s*=\s*("([^"]*)"|'([^']*)')/g,
        (match, name, _quoted, dq, sq) => {
          const value = dq ?? sq
          return isSecret(name) && value !== ''
            ? `${name}="${REDACTED}"`
            : match
        }
      )
      // "password": "value"
      .replace(/("([\w.-]+)"\s*:\s*)"([^"]*)"/g, (match, prefix, name, value) =>
        isSecret(name) && value !== '' ? `${prefix}"${REDACTED}"` : match
      )
  )
}
